// src/BlitzHowTo/tutorialRegistry.js
import routes from '../Config/routes';

/**
 * Tutorial step configuration:
 * - title: Step title
 * - content: Explanation text
 * - target: CSS selector for element to highlight
 * - placement: 'top' | 'bottom' | 'left' | 'right' | 'center'
 * - icon: Icon name
 * - action: Optional action to perform (e.g., open a dialog)
 * - waitFor: Optional selector to wait for before proceeding
 */

export const tutorialRegistry = {
  // ═══════════════════════════════════════════════════════════
  // CREATOR DASHBOARD - Uses same component as regular dashboard
  // but with creator-focused messaging and navigation targets
  // ═══════════════════════════════════════════════════════════
  [routes.creatorConnectStart]: {
    id: 'creator-dashboard-intro-v1',
    title: 'Welcome to Your Creator Hub',
    description: 'Let\'s show you around your creator command center',
    steps: [
      {
        title: 'For You Feed',
        content: 'Discover campaigns, collaborations, and content curated specifically for your niche and audience.',
        target: '[data-tour="for-you-tab"]',
        placement: 'bottom',
        icon: 'star',
      },
      {
        title: 'Your Bubbles',
        content: 'Join creator communities and topic-based groups to network and collaborate with other creators.',
        target: '[data-tour="bubbles-tab"]',
        placement: 'bottom',
        icon: 'groups',
      },
      {
        title: 'Your Activity Feed',
        content: 'This is your personalized activity feed showing updates, opportunities, and what\'s happening in your creator network.',
        target: '[data-tour="activity-feed"]',
        placement: 'bottom',
        icon: 'explore',
      },
      {
        title: 'Your Profile & Bio',
        content: 'Click your avatar to view and edit your public creator bio that brands and collaborators can see.',
        target: '.MuiAvatar-root',
        placement: 'right',
        icon: 'account_circle',
      },
      {
        title: 'Notifications Hub',
        content: 'Stay on top of deal offers, payment updates, messages, and important alerts all in one place.',
        target: 'button[aria-label="Notifications"], button:has(> .MuiBadge-root > svg[data-testid="NotificationsNoneIcon"])',
        placement: 'right',
        icon: 'notifications',
      },
      {
        title: 'Your Achievements',
        content: 'Check out your Trophy Room to see all the milestones, badges, and rewards you\'ve earned!',
        target: 'button:has(> svg[data-testid="EmojiEventsIcon"])',
        placement: 'right',
        icon: 'trophy',
      },
      {
        title: 'Creator Settings',
        content: 'Manage your account, payment methods, notification preferences, and more.',
        target: 'button:has(> svg[data-testid="SettingsOutlinedIcon"])',
        placement: 'right',
        icon: 'settings',
      },
      {
        title: 'Navigate Your Tools',
        content: 'Use the sidebar to quickly access Money, Deals, Chat, Partner Suite, and all your creator tools.',
        target: '#NavbarContainer',
        placement: 'right',
        icon: 'menu',
      },
      {
        title: 'You\'re Ready to Create!',
        content: 'You now know the essentials of your Creator Hub. Start exploring to unlock opportunities and grow your brand!',
        target: null,
        placement: 'center',
        icon: 'check_circle',
      },
    ],
  },

  [routes.dashboard]: {
    id: 'dashboard-intro-v2',
    title: 'Welcome to Your Blitz Dashboard',
    description: 'Let\'s take a quick tour of your command center',
    steps: [
      {
        title: 'Your Activity Feed',
        content: 'This is where you\'ll see personalized updates, opportunities, and what\'s trending in your network.',
        target: '[data-tour="activity-feed"]',
        placement: 'bottom',
        icon: 'explore',
      },
      {
        title: 'For You Tab',
        content: 'Discover content, campaigns, and creators curated specifically for your interests and goals.',
        target: '[data-tour="for-you-tab"]',
        placement: 'bottom',
        icon: 'star',
      },
      {
        title: 'Your Bubbles',
        content: 'Join topic-based communities to connect with others who share your interests.',
        target: '[data-tour="bubbles-tab"]',
        placement: 'bottom',
        icon: 'groups',
      },
      {
        title: 'Your Profile & Bio',
        content: 'Click your avatar anytime to view and edit your professional bio that others can see.',
        target: '.MuiAvatar-root',
        placement: 'right',
        icon: 'account_circle',
      },
      {
        title: 'Notifications Center',
        content: 'Stay updated with all your important alerts, messages, and activity notifications in one place.',
        target: 'button[aria-label="Notifications"], button:has(> .MuiBadge-root > svg[data-testid="NotificationsNoneIcon"])', 
        placement: 'right',
        icon: 'notifications',
      },
      {
        title: 'Trophy Room',
        content: 'Visit your Trophy Room to see all your achievements, milestones, and rewards earned on Blitz!',
        target: 'button:has(> svg[data-testid="EmojiEventsIcon"])',
        placement: 'right',
        icon: 'trophy',
      },
      {
        title: 'Account Settings',
        content: 'Customize your experience: update preferences, manage payment methods, and configure notifications.',
        target: 'button:has(> svg[data-testid="SettingsOutlinedIcon"])',
        placement: 'right',
        icon: 'settings',
      },
      {
        title: 'Navigation Menu',
        content: 'Use the sidebar to quickly navigate between Dashboard, BlitzPay, Collaborations, Chat, and more.',
        target: '#NavbarContainer',
        placement: 'right',
        icon: 'menu',
      },
      {
        title: 'You\'re All Set!',
        content: 'You now know the essentials of navigating Blitz. Explore each section to unlock the full power of the platform!',
        target: null,
        placement: 'center',
        icon: 'check_circle',
      },
    ],
  },

  [routes.blitzpay]: {
    id: 'blitzpay-intro-v1',
    title: 'BlitzPay: Instant Payments Made Easy',
    description: 'Your complete guide to sending and receiving payments',
    steps: [
      {
        title: 'Payment Dashboard',
        content: 'View your balance, pending transactions, and payment history all in one place.',
        target: '[data-tour="payment-dashboard"]',
        placement: 'right',
        icon: 'payment',
      },
      {
        title: 'Send Money',
        content: 'Click here to send instant payments to creators, vendors, or anyone in your network.',
        target: '[data-tour="send-payment-btn"]',
        placement: 'bottom',
        icon: 'send',
      },
      {
        title: 'Payment Methods',
        content: 'Connect your bank account, PayPal, or use USDC for instant settlements.',
        target: '[data-tour="payment-methods"]',
        placement: 'left',
        icon: 'account_balance',
      },
      {
        title: 'Transaction History',
        content: 'Track every payment with detailed records, receipts, and status updates.',
        target: '[data-tour="transaction-history"]',
        placement: 'top',
        icon: 'receipt',
      },
      {
        title: 'Invoice Factoring',
        content: 'Get paid instantly on your invoices instead of waiting 30-60 days. We advance you the funds!',
        target: '[data-tour="invoice-factoring"]',
        placement: 'right',
        icon: 'flash_on',
      },
    ],
  },

  [routes.creatorConnectPayoutsInvoicing]: {
    id: 'creator-money-intro-v1',
    title: 'Creator Money Hub',
    description: 'Manage your earnings like a pro',
    steps: [
      {
        title: 'Your Earnings Overview',
        content: 'See your total earnings, pending payouts, and payment history at a glance.',
        target: '[data-tour="earnings-overview"]',
        placement: 'right',
        icon: 'payments',
      },
      {
        title: 'Transfer Money',
        content: 'Pay someone on platform instantly! All payouts stem from your existing blitz balance.',
        target: '[data-tour="request-payout-btn"]',
        placement: 'bottom',
        icon: 'attach_money',
      },
      {
        title: 'Send Invoices',
        content: 'Create professional invoices for brand partnerships. We can even advance you the money!',
        target: '[data-tour="create-invoice-btn"]',
        placement: 'bottom',
        icon: 'receipt_long',
      },
      {
        title: 'Payment Settings',
        content: 'Connect your payment methods and set up automatic payouts on your schedule.',
        target: '[data-tour="payment-settings"]',
        placement: 'left',
        icon: 'settings',
      },
      {
        title: 'Transaction History',
        content: 'Every payment is tracked with receipts, timestamps, and tax-ready documentation.',
        target: '[data-tour="transaction-list"]',
        placement: 'top',
        icon: 'history',
      },
    ],
  },

  [routes.agency]: {
    id: 'agency-intro-v3',
    title: 'Welcome to Your Agency Dashboard',
    description: 'Everything you need to manage your creator roster',
    steps: [
      {
        title: 'Agency Command Center',
        content: 'This is your headquarters! Monitor your entire roster\'s performance, earnings, and active campaigns from one central location.',
        target: '[data-tour="agency-overview"]',
        placement: 'bottom',
        icon: 'dashboard',
      },
      {
        title: 'Live Activity Feed',
        content: 'See real-time updates as they happen: new deals, payments processed, collaborations started, and creator milestones achieved.',
        target: '[data-tour="agency-activity-feed"]',
        placement: 'right',
        icon: 'trending_up',
      },
      {
        title: 'Deal Flow Panel',
        content: 'Track incoming opportunities and manage your pipeline. Click any creator to see their detailed activity, deals, and earnings.',
        target: '[data-tour="agency-deal-flow"]',
        placement: 'left',
        icon: 'handshake',
      },
      {
        title: 'Quick Roster Actions',
        content: 'View all creators in your roster or add new talent with one click. Keep your agency growing!',
        target: '[data-tour="roster-list"]',
        placement: 'bottom',
        icon: 'people',
      },
      {
        title: 'You\'re Ready!',
        content: 'You now know the essentials of managing your agency on Blitz. Explore each section to maximize your team\'s potential!',
        target: null,
        placement: 'center',
        icon: 'check_circle',
      },
    ],
  },

  [routes.agencyCreators]: {
    id: 'agency-creators-v1',
    title: 'Manage Your Creator Roster',
    description: 'View and manage all creators in your agency',
    steps: [
      {
        title: 'Creator Roster Sidebar',
        content: 'Browse your full creator roster here. Use the search bar at the top to quickly find specific creators by name or username.',
        target: '[data-tour="agency-creators-sidebar"]',
        placement: 'right',
        icon: 'people',
      },
      {
        title: 'Creator Detail Panel',
        content: 'Click any creator to see their complete profile, activity timeline, active deals, and payment history all in one view.',
        target: '[data-tour="agency-creator-detail"]',
        placement: 'left',
        icon: 'account_circle',
      },
      {
        title: 'Collapsible Navigation',
        content: 'Use the arrow button to collapse the sidebar and maximize your workspace when reviewing detailed creator information.',
        target: '[data-tour="agency-creators-sidebar"]',
        placement: 'right',
        icon: 'menu',
      },
      {
        title: 'All Set!',
        content: 'You\'re ready to manage your roster! Click any creator to dive into their details.',
        target: null,
        placement: 'center',
        icon: 'check_circle',
      },
    ],
  },

  [routes.agencyAllMoney]: {
    id: 'agency-money-v2',
    title: 'Agency Payments & Earnings',
    description: 'Manage all creator payouts and agency commissions',
    steps: [
      {
        title: 'Payment Dashboard Overview',
        content: 'Your complete financial command center. See total earnings, pending amounts, and claimable funds at a glance.',
        target: '[data-tour="agency-money-overview"]',
        placement: 'bottom',
        icon: 'payments',
      },
      {
        title: 'Your Agency Balance',
        content: 'Click here to view your current Blitz balance and see a detailed transaction history. This is money ready to withdraw!',
        target: '[data-tour="agency-balance"]',
        placement: 'bottom',
        icon: 'account_balance',
      },
      {
        title: 'Claimable Payouts',
        content: 'These are completed creator deals where you can claim your commission right now. One click to add funds to your balance!',
        target: '[data-tour="agency-claimable"]',
        placement: 'bottom',
        icon: 'monetization_on',
      },
      {
        title: 'Payment Configuration',
        content: 'Configure your bank accounts, PayPal, and tax information here. Required before you can withdraw funds to your accounts.',
        target: '[data-tour="agency-payment-methods"]',
        placement: 'top',
        icon: 'credit_card',
      },
      {
        title: 'Payment Status Tabs',
        content: 'Switch between Claimable (ready now), Pending (waiting on conditions), Completed (already claimed), and All payouts.',
        target: '[data-tour="agency-payment-tabs"]',
        placement: 'top',
        icon: 'tab',
      },
      {
        title: 'You\'re All Set!',
        content: 'You now understand how agency payments work. Click "Claimable" to start collecting your commissions!',
        target: null,
        placement: 'center',
        icon: 'check_circle',
      },
    ],
  },

  [routes.agencyAllCollabs]: {
    id: 'agency-collabs-v1',
    title: 'All Agency Collaborations',
    description: 'Track all creator campaigns and partnerships',
    steps: [
      {
        title: 'Collaboration Hub',
        content: 'View all active campaigns and partnerships across your entire roster in one centralized dashboard.',
        target: '[data-tour="agency-collabs-overview"]',
        placement: 'bottom',
        icon: 'handshake',
      },
      {
        title: 'Advanced Filters',
        content: 'Use these filters to find specific collaborations by creator, brand, status, price range, or deadline.',
        target: '[data-tour="agency-collabs-filters"]',
        placement: 'bottom',
        icon: 'search',
      },
      {
        title: 'Collaboration Cards',
        content: 'Each card shows key details: creator, brand, deliverables, deadlines, and current status. Click to see full details.',
        target: '[data-tour="agency-collabs-cards"]',
        placement: 'right',
        icon: 'info',
      },
      {
        title: 'Ready to Go!',
        content: 'You can now track and manage all your roster\'s deals from one place!',
        target: null,
        placement: 'center',
        icon: 'check_circle',
      },
    ],
  },

  [routes.agencyRosterSettings]: {
    id: 'agency-roster-settings-v1',
    title: 'Roster Settings & Configuration',
    description: 'Configure your agency roster preferences',
    steps: [
      {
        title: 'Roster Settings',
        content: 'Configure agency-wide settings, commission rates, and roster management preferences here.',
        target: '[data-tour="roster-settings-panel"]',
        placement: 'right',
        icon: 'settings',
      },
      {
        title: 'Commission Structure',
        content: 'Set default commission rates and payment splits for your agency roster.',
        target: '[data-tour="commission-settings"]',
        placement: 'bottom',
        icon: 'monetization_on',
      },
      {
        title: 'All Done!',
        content: 'Your roster settings are configured. Changes will apply to all creators in your roster.',
        target: null,
        placement: 'center',
        icon: 'check_circle',
      },
    ],
  },
};

export const getTutorialForRoute = (pathname) => {
  if (tutorialRegistry[pathname]) {
    return tutorialRegistry[pathname];
  }
  
  const matchingRoute = Object.keys(tutorialRegistry).find(route => 
    pathname.startsWith(route)
  );
  
  return matchingRoute ? tutorialRegistry[matchingRoute] : null;
};

export const hasTutorial = (pathname) => {
  return getTutorialForRoute(pathname) !== null;
};